import type { IHeaderLinkItem, IHeaderUserMenuItem } from "~/types/components/header.type";
import type { IPromotionData } from "~/types/global/result.type";

export const useHeaderData = () => {
    const { t } = useI18n();
    const { isDesktop } = useDevice();

    const promotionalModalData: IPromotionData = {
        title: t("components_header_promotional_title"),
        description: t("components_header_promotional_description"),
        options: [
            {
                text: t("components_header_promotional_option_1"),
                icon: "check",
            },
            {
                text: t("components_header_promotional_option_6"),
                icon: "check",
            },
            {
                text: t("components_header_promotional_option_2"),
                icon: "check",
            },
            {
                text: t("components_header_promotional_option_3"),
                icon: "check",
            },
            {
                text: t("components_header_promotional_option_4"),
                icon: "check",
            },
            {
                text: t("components_header_promotional_option_5"),
                icon: "check",
            },
        ],
        backgroundImages: {
            desktopSrc: "https://revi.rvstatic.com/aihomedesign/images-v3/Result-page/upgrage-banner-desktop.webp",
            tabletSrc: "https://revi.rvstatic.com/aihomedesign/images-v3/Result-page/upgrage-banner-tablet.webp",
            mobileSrc: "https://revi.rvstatic.com/aihomedesign/images-v3/Result-page/upgrage-banner-mobile.webp",
            desktopBlurSrc:
                "https://revi.rvstatic.com/aihomedesign/images-v3/Result-page/upgrage-banner-desktop-blur.webp",
            tabletBlurSrc:
                "https://revi.rvstatic.com/aihomedesign/images-v3/Result-page/upgrage-banner-tablet-blur.webp",
            mobileBlurSrc:
                "https://revi.rvstatic.com/aihomedesign/images-v3/Result-page/upgrage-banner-mobile-blur.webp",
        },
        cta: {
            text: t("components_header_promotional_cta_text"),
            link: "/pricing",
            id: "resultpage-seeplans-modal-button",
        },
    };

    const headerNavigation: IHeaderLinkItem[] = [
        {
            title: t("components_header_VS_title"),
            link: "/virtual-staging",
            hasMegaMenu: useDevice().isMobile ? true : false,
            subMenu: [
                {
                    title: t("global_serviceName_virtualStaging"),
                    link: "/virtual-staging",
                },
                {
                    title: t("global_serviceName_humanVirtualStaging"),
                    link: `/virtual-staging${isDesktop ? "#virtual-staging-by-human" : ""}`,
                    elementId: "#virtual-staging-by-human",
                },
            ],
        },
        {
            title: t("components_header_PE_title"),
            link: "/photo-editing",
            hasMegaMenu: useDevice().isMobile ? true : false,
            subMenu: [
                {
                    title: t("global_serviceName_dayToDusk"),
                    link: "/day-to-dusk",
                    elementId: "#day-to-dusk",
                },
                {
                    title: t("global_serviceName_itemRemoval"),
                    link: "/item-removal",
                    elementId: "#item-removal",
                },
                {
                    title: t("global_serviceName_imageEnhancement"),
                    link: "/image-enhancement",
                    elementId: "#image-enhancement",
                },
            ],
        },
        {
            hasMegaMenu: true,
            title: t("components_header_HR"),
            link: "/interior-design",
            subMenu: [
                {
                    title: t("components_header_ID_title"),
                    link: "/interior-design",
                    description: t("components_header_ID_desc"),
                    icon: "https://revi.rvstatic.com/aihomedesign/header-icons/VS.svg",
                    hasMegaMenu: true,
                    subMenu: [
                        {
                            title: t("global_serviceName_creativeDesigner"),
                            link: `/interior-design${isDesktop ? "#ai-interior-designer" : ""}`,
                            elementId: "#ai-interior-designer",
                        },
                        {
                            title: t("global_serviceName_furnitureReplacement"),
                            link: `/interior-design${isDesktop ? "#furniture-replacement" : ""}`,
                            elementId: "#furniture-replacement",
                        },
                        {
                            title: t("global_serviceName_fullSetReplacement"),
                            link: `/interior-design${isDesktop ? "#full-set-replacement" : ""}`,
                            elementId: "#full-set-replacement",
                        },
                    ],
                },
                {
                    title: t("components_header_HR_title"),
                    link: "/home-renovation",
                    hasMegaMenu: true,
                    description: t("components_header_HR_desc"),
                    subMenu: [
                        {
                            title: t("global_serviceName_wallChange"),
                            link: `/home-renovation${isDesktop ? "#wall-change" : ""}`,
                            elementId: "#wall-change",
                        },
                        {
                            title: t("global_serviceName_floorChange"),
                            link: `/home-renovation${isDesktop ? "#floor-change" : ""}`,
                            elementId: "#floor-change",
                        },
                        {
                            title: t("global_serviceName_ceilingChange"),
                            link: `/home-renovation${isDesktop ? "#ceiling-change" : ""}`,
                            elementId: "#ceiling-change",
                        },
                        {
                            title: t("global_serviceName_backSplashChange"),
                            link: `/home-renovation${isDesktop ? "#backsplash-change" : ""}`,
                            elementId: "#backsplash-change",
                        },
                        {
                            title: t("global_serviceName_underConstruction"),
                            link: `/home-renovation${isDesktop ? "#under-construction" : ""}`,
                            elementId: "#under-construction",
                        },
                    ],
                },
            ],
        },
        {
            // hasMegaMenu: useDevice().isMobile ? true : false,
            title: t("components_header_b2bSolutions"),
            // subMenu: [
            //     {
            //         title: t("components_header_b2b"),
            //         link: "/b2b",
            //     },
            //     {
            //         title: t("components_footer_affiliateProgram"),
            //         link: "/affiliate-program",
            //     },
            //     {
            //         title: "API",
            //         link: "/b2b#api-access",
            //     },
            // ],
            link: "/b2b",
        },

        {
            title: t("components_header_pricing"),
            link: "/pricing",
        },
    ];

    const userMenuItems: IHeaderUserMenuItem[] = [
        {
            title: t("components_header_generatedPhotos"),
            slug: "generated-photos",
            icon: "tabler:photo",
            link: "/generated-photos",
        },
        {
            title: t("components_header_billing"),
            slug: "billing",
            icon: "tabler:license",
            link: "/billing",
        },
        {
            title: t("components_header_apiAccess"),
            slug: "api",
            icon: "tabler:key",
            link: "/api-access",
        },
        {
            title: t("components_header_members"),
            slug: "members",
            icon: "tabler:vector-triangle",
            link: "/sub-account/members",
        },
        {
            title: t("components_header_account"),
            slug: "account",
            icon: "tabler:user",
            link: "/account",
        },
        {
            title: t("components_header_logout"),
            slug: "logout",
            icon: "tabler:logout",
        },
    ];

    return { headerNavigation, userMenuItems, promotionalModalData };
};
